import { Trans } from '@lingui/react/macro';
import { Box, Stack } from '@mui/material';

import { SnackbarMessage, SnackbarOrigin, VariantType } from 'notistack';
import { OperationResult } from 'urql';

import type { SnackbarType } from '../hooks/useSnackbar';
import CircularProgress from '@watershed/ui-core/components/CircularProgress';
import isAdminApp from '@watershed/ui-core/utils/isAdminApp';

type MutationSnackbarOptions<Data> = {
  errorMessage?: SnackbarMessage | ((error: Error) => SnackbarMessage);
  successMessage?: SnackbarMessage | ((data: Data) => SnackbarMessage);
  fallbackErrorMessage?: SnackbarMessage;
  /**
   * Optional callback which is run immediately iff the mutation returns data
   * successfully. Mainly for lightweight post-success actions, like setting
   * state or redirecting. The benefit of this is this function encapsulates
   * the logic to determine whether the `data` is there, while the downside is
   * that it's not snackbar-related.
   *
   * It would be better if we fixed our urql types to require `data` if there
   * is no `error`! But this is convenient for now.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data: Data) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: Error) => any;
  // override snackbar's styling
  successVariant?: VariantType;
  errorVariant?: VariantType;

  // Note that this duration is doubled for errors.
  autoHideDuration?: number | null;
  anchorOrigin?: SnackbarOrigin;
};

export function snackbarErrorMessage<Data>(
  result: OperationResult<Data>,
  isAdminApp: boolean,
  {
    errorMessage,
    successMessage,
    fallbackErrorMessage,
    onSuccess,
    onError,
    successVariant = 'success',
    errorVariant = 'error',
    autoHideDuration,
    anchorOrigin,
  }: MutationSnackbarOptions<Data>
): SnackbarMessage {
  // If this is specifically a `UserInputError` / `BAD_USER_INPUT`, then
  // we explicitly do want to show that error message to the user.
  const userErrorInputMessage =
    result.error?.graphQLErrors?.[0]?.extensions.code === 'BAD_USER_INPUT'
      ? result.error.graphQLErrors[0].message
      : undefined;

  const defaultErrorMessage = (
    <Trans context="error">
      Something went wrong. Please try again, and if the issue continues,
      contact Watershed.
    </Trans>
  );

  const error =
    result.error || new Error('No error returned, but no data found.');

  const errorMessageFromProps =
    typeof errorMessage === 'function' ? errorMessage(error) : errorMessage;

  // If we're in the admin app, then it's okay to default to showing the
  // raw error message from the API.  If not, then be conservative and show the generic error message.
  const adminErrorMessage = isAdminApp ? error.message : undefined;

  return (
    errorMessageFromProps ??
    adminErrorMessage ??
    userErrorInputMessage ??
    fallbackErrorMessage ??
    defaultErrorMessage
  );
}

// Ideally this could be a hook itself and would define its own snackbar. After
// upgrading notistack to >2.0, useSnackbar() is returning null when called
// within a hook, so we've changed away from that to fix the bugs until we can
// figure out why that's happening.
export default async function enqueueMutationSnackbar<Data>(
  snackbar: SnackbarType,
  result: OperationResult<Data>,
  {
    errorMessage,
    successMessage,
    fallbackErrorMessage,
    onSuccess,
    onError,
    successVariant = 'success',
    errorVariant = 'error',
    autoHideDuration,
    anchorOrigin,
  }: MutationSnackbarOptions<Data> = {}
): Promise<void> {
  if (result.error || !result.data) {
    const error =
      result.error || new Error('No error returned, but no data found.');

    console.error(error);

    const isUsingAdminApp = isAdminApp();

    const snackbarError = snackbarErrorMessage(result, isUsingAdminApp, {
      errorMessage,
      fallbackErrorMessage,
    });

    snackbar.enqueueSnackbar(snackbarError, {
      variant: errorVariant,
      autoHideDuration: autoHideDuration ? autoHideDuration * 2 : undefined,
      anchorOrigin,
    });
    await onError?.(error);
  } else {
    if (successMessage) {
      snackbar.enqueueSnackbar(
        typeof successMessage === 'function'
          ? successMessage(result.data)
          : successMessage,
        { variant: successVariant, autoHideDuration, anchorOrigin }
      );
    }

    if (onSuccess) {
      await onSuccess(result.data);
    }
  }
}

/**
 * A variant of enqueue mutation snackbar that shows a loading message while the
 * mutation is in flight. This is useful for mutations that take a long time.
 */
export async function enqueueMutationSnackbarWithLoadingMessage<Data>(
  snackbar: SnackbarType,
  resultPromise: Promise<OperationResult<Data>>,
  options: MutationSnackbarOptions<Data> & {
    loadingMessage: SnackbarMessage;
  }
): Promise<void> {
  const snackbarKey = snackbar.enqueueSnackbar(
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      {options.loadingMessage}
      <Box display="flex">
        <CircularProgress size={16} color="inherit" variant="indeterminate" />
      </Box>
    </Stack>,
    {
      autoHideDuration: null,
    }
  );
  const result = await resultPromise;
  snackbar.closeSnackbar(snackbarKey);
  return enqueueMutationSnackbar(snackbar, result, options);
}
